import { ChevronRightIcon } from '@heroicons/react/16/solid';

import { Logo } from '@/components/Logo';
import { CBBIcon, CFBIcon, NFLIcon } from '@/pages/order/components/ProductIcons';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

// All available products, so we can list every active sport + its lowest price.
const getProducts: RequestSchema<API.MembershipProduct[]> = {
  name: 'appLandingProducts',
  input: '/api/v1/product',
};

// Compact service rows — sport + route + a short line. Order controls display.
const SPORTS = [
  { sportId: 3, name: 'NFL', route: '/order/nfl', tagline: 'Sides, totals & halves', Icon: NFLIcon },
  {
    sportId: 1,
    name: 'College Football',
    route: '/order/cfb',
    tagline: 'FBS & FCS sides & totals',
    Icon: CFBIcon,
  },
  {
    sportId: 2,
    name: 'College Basketball',
    route: '/order/cbb',
    tagline: 'Sides & totals through March',
    Icon: CBBIcon,
  },
];

function isOfferVisible(offer: API.MembershipProductOffer): boolean {
  return offer?.hidden !== true && (offer as any)?.available !== false && offer?.price != null;
}

/**
 * Simplified landing shown to logged-out users inside the native app, in place of
 * the full marketing page. A full-height welcome screen — brand + services up top,
 * stacked auth buttons pinned to the bottom. Web is unaffected; IndexRoute only
 * renders this on native.
 */
export default function AppLanding() {
  const productsRequest = useRequest(getProducts);
  const products = productsRequest.data;

  const services = SPORTS.map((sport) => {
    const prices = (products ?? [])
      .filter((p) => p.sportIds?.includes(sport.sportId))
      .flatMap((p) => p.offers ?? [])
      .filter(isOfferVisible)
      .map((o) => o.price)
      .filter((p): p is number => typeof p === 'number' && p > 0);
    return { ...sport, price: prices.length ? Math.min(...prices) : null };
  }).filter((s) => s.price != null);

  return (
    <div className="flex min-h-[100dvh] flex-col bg-white px-5 pt-[max(2rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] text-zinc-950 dark:bg-zinc-950 dark:text-white">
      {/* Brand + pitch */}
      <div className="text-center">
        <div className="flex justify-center">
          <Logo className="h-16 w-auto select-none" />
        </div>
        <h1 className="mt-6 text-3xl font-bold tracking-tight">Sharp picks since 1997.</h1>
        <p className="mx-auto mt-2 max-w-xs text-sm text-zinc-500 dark:text-zinc-400">
          56% ATS long-term, independently verified by betstamp.
        </p>
      </div>

      {/* Services */}
      {services.length > 0 && (
        <div className="mt-8">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
            Available services
          </p>
          <div className="flex flex-col gap-2">
            {services.map((s) => (
              <a
                key={s.sportId}
                href={s.route}
                className="flex items-center gap-3 rounded-xl border border-zinc-950/10 px-4 py-3 transition-colors hover:bg-zinc-50 dark:border-white/10 dark:hover:bg-white/5"
              >
                <s.Icon className="size-8 shrink-0 fill-emerald-600 dark:fill-emerald-400" />
                <div className="min-w-0 flex-1 text-left">
                  <div className="text-sm font-semibold">{s.name}</div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">{s.tagline}</div>
                </div>
                <div className="shrink-0 text-sm text-zinc-500 dark:text-zinc-400">from ${s.price}</div>
                <ChevronRightIcon className="size-4 shrink-0 text-zinc-300 dark:text-zinc-600" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Auth — pinned to the bottom, stacked full-width */}
      <div className="mt-auto flex flex-col gap-2.5 pt-10">
        <a
          href="/register"
          className="rounded-xl bg-emerald-600 py-3.5 text-center text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
        >
          Create a free account
        </a>
        <a
          href="/signin"
          className="rounded-xl py-3.5 text-center text-sm font-semibold text-zinc-700 ring-1 ring-zinc-950/15 transition hover:bg-zinc-50 dark:text-zinc-200 dark:ring-white/15 dark:hover:bg-white/10"
        >
          Sign in
        </a>
        <p className="text-center text-xs text-zinc-400 dark:text-zinc-500">
          Free account — get free plays and service updates.
        </p>
      </div>
    </div>
  );
}
